import { reportClientError } from '../lib/errors.ts';
import { toast } from '../state/toast.ts';

// Spraakfouten (dicteren met Scribe, voorlezen via ElevenLabs) als één Nederlandse zin: wat er aan de hand is en
// wat Jeroen kan doen. Ruwe meldingen ("Spraakherkenning gestopt (1006)", "NotAllowedError") komen zo niet meer op
// het scherm; wat onbekend is gaat wel naar het foutenregister. Elke fout geeft één melding, nooit twee.

export type VoiceErrorContext = 'dictation' | 'speech';

export const VOICE_ERROR_KINDS = ['off', 'rejected', 'denied', 'no-mic', 'mic-busy', 'unsupported', 'autoplay', 'playback', 'offline', 'network', 'expired', 'busy', 'service', 'login', 'unknown'] as const;
export type VoiceErrorKind = (typeof VOICE_ERROR_KINDS)[number];

/** Een spraakfout waarvan de soort al vaststaat (de websocket van Scribe, een audio-element dat niet speelt). */
export class VoiceError extends Error {
  constructor(
    public readonly kind: VoiceErrorKind,
    detail?: string,
  ) {
    super(detail ?? `spraakfout: ${kind}`);
    this.name = 'VoiceError';
  }
}

const DICTATION: Record<VoiceErrorKind, string> = {
  off: 'Spraak staat uit: er is geen ElevenLabs-sleutel ingesteld.',
  rejected: 'De spraakdienst weigert de ElevenLabs-sleutel (ongeldig of tegoed op); typen kan wel.',
  denied: 'Geen toestemming voor de microfoon; zet die aan in de instellingen van je browser.',
  'no-mic': 'Er is geen microfoon gevonden; typen kan wel.',
  'mic-busy': 'De microfoon is in gebruik door een andere app; sluit die en probeer het opnieuw.',
  unsupported: 'Deze browser geeft geen toegang tot de microfoon (dat kan alleen via https in een recente browser).',
  autoplay: 'Er ging iets mis met de spraakherkenning; probeer het opnieuw.',
  playback: 'Er ging iets mis met de spraakherkenning; probeer het opnieuw.',
  offline: 'Je bent offline; dicteren werkt weer zodra er verbinding is.',
  network: 'Jerome is even niet bereikbaar; probeer de microfoon zo opnieuw.',
  expired: 'De sessie van de spraakherkenning is verlopen; start de microfoon opnieuw.',
  busy: 'De spraakdienst is even overbelast; probeer het zo opnieuw.',
  service: 'De spraakherkenning is nu niet bereikbaar; typen kan wel.',
  login: 'Je bent niet meer ingelogd; log opnieuw in.',
  unknown: 'Er ging iets mis met de spraakherkenning; probeer het opnieuw.',
};

const SPEECH: Record<VoiceErrorKind, string> = {
  off: 'Voorlezen staat uit: er is geen ElevenLabs-sleutel ingesteld.',
  rejected: 'Voorlezen lukt niet: de spraakdienst weigert de ElevenLabs-sleutel (ongeldig of tegoed op).',
  denied: 'De browser hield het afspelen tegen; probeer het nog een keer.',
  'no-mic': 'De audio van het voorlezen kon niet worden afgespeeld.',
  'mic-busy': 'De audio van het voorlezen kon niet worden afgespeeld.',
  unsupported: 'Deze browser kan de audio van Jerome niet afspelen.',
  autoplay: 'De browser hield het afspelen tegen; probeer het nog een keer.',
  playback: 'De audio van het voorlezen kon niet worden afgespeeld.',
  offline: 'Je bent offline; voorlezen werkt weer zodra er verbinding is.',
  network: 'Jerome is even niet bereikbaar; probeer het voorlezen zo opnieuw.',
  expired: 'Voorlezen lukt nu niet; probeer het later opnieuw.',
  busy: 'De spraakdienst is even overbelast; probeer het voorlezen zo opnieuw.',
  service: 'Voorlezen lukt nu niet: de spraakdienst is niet bereikbaar.',
  login: 'Je bent niet meer ingelogd; log opnieuw in.',
  unknown: 'Voorlezen lukt nu niet; probeer het later opnieuw.',
};

// Namen van DOMException's: bij dicteren gaan ze over de microfoon (getUserMedia), bij voorlezen over afspelen.
const DENIED = ['NotAllowedError', 'PermissionDeniedError', 'SecurityError'];
const NO_MIC = ['NotFoundError', 'DevicesNotFoundError', 'OverconstrainedError'];
const MIC_BUSY = ['NotReadableError', 'TrackStartError', 'AbortError'];
const FETCH_FAILED = /failed to fetch|load failed|networkerror|network error|network request failed/i;

function field(e: unknown, key: 'name' | 'message' | 'status'): unknown {
  return typeof e === 'object' && e !== null && key in e ? (e as Record<string, unknown>)[key] : undefined;
}

export function voiceErrorKind(e: unknown, ctx: VoiceErrorContext): VoiceErrorKind {
  if (e instanceof VoiceError) return e.kind;
  const name = String(field(e, 'name') ?? '');
  const message = typeof e === 'string' ? e : String(field(e, 'message') ?? '');
  if (DENIED.includes(name)) return ctx === 'speech' ? 'autoplay' : 'denied';
  if (NO_MIC.includes(name)) return ctx === 'speech' ? 'playback' : 'no-mic';
  if (MIC_BUSY.includes(name)) return ctx === 'speech' ? 'playback' : 'mic-busy';
  if (name === 'NotSupportedError') return 'unsupported';
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return 'offline';
  const status = field(e, 'status');
  if (typeof status === 'number') {
    // "de server gaf status …" is de kale tekst van api.ts: geen antwoord van Jerome zelf maar van de hosting.
    const bare = /^de server gaf status/i.test(message);
    if (status === 401) return 'login';
    if (status === 429) return 'busy';
    if (status >= 500 && bare) return 'network';
    if (status === 503) return 'off';
    if (/status 40[123]\b/.test(message)) return 'rejected';
    if (/status 429\b/.test(message)) return 'busy';
    return status >= 500 ? 'service' : 'unknown';
  }
  if (FETCH_FAILED.test(message)) return 'network';
  return 'unknown';
}

/** Eén zin voor Jeroen: wat er aan de hand is en wat hij kan doen. */
export function describeVoiceError(e: unknown, ctx: VoiceErrorContext): string {
  return (ctx === 'speech' ? SPEECH : DICTATION)[voiceErrorKind(e, ctx)];
}

const REPORTED: ReadonlySet<VoiceErrorKind> = new Set(['unknown', 'service']);

/** Onbekende fouten en storingen bij de spraakdienst gaan naar het foutenregister; bekende gevallen (geen sleutel, geen toestemming) niet. */
export function reportVoiceError(e: unknown, ctx: VoiceErrorContext): void {
  const kind = voiceErrorKind(e, ctx);
  if (REPORTED.has(kind)) void reportClientError(e, { where: ctx === 'speech' ? 'voorlezen' : 'dicteren', kind });
}

export const VOICE_TOAST_MS = 6000;
const shown = new Map<string, number>();

/** Eén melding per spraakfout: dezelfde tekst komt niet nog eens zolang de vorige melding nog staat. */
export function voiceToast(text: string): void {
  const now = Date.now();
  const last = shown.get(text);
  if (last !== undefined && now - last < VOICE_TOAST_MS) return;
  shown.set(text, now);
  toast(text, { tone: 'warn', ms: VOICE_TOAST_MS });
}

/** Beschrijft de fout, meldt een onbekende aan het foutenregister en toont één melding; geeft de tekst terug. */
export function showVoiceError(e: unknown, ctx: VoiceErrorContext): string {
  reportVoiceError(e, ctx);
  const text = describeVoiceError(e, ctx);
  voiceToast(text);
  return text;
}

/** Voor tests: vergeet welke meldingen al getoond zijn. */
export function resetVoiceToastsForTests(): void {
  shown.clear();
}
